import { inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { UserCommandService } from 'app/modules/users/api/commands/user-command.service';
import { UpdateUserCommand } from 'app/modules/users/api/commands/user-command.types';
import { UserQueryService } from 'app/modules/users/api/queries/user-query.service';
import { hasError, isExecuting, withClearState, withOpTracking } from 'app/shared/store';
import { User } from 'app/shared/types/user.types';
import { firstValueFrom, switchMap } from 'rxjs';

enum UserOpType {
    LoadUser = 'loadUser',
    UpdateUser = 'updateUser',
}

interface UserState {
    user: User | null;
}

const initialState: UserState = {
    user: null,
};

export const UserStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withClearState(initialState),
    ...withOpTracking(UserOpType),
    withComputed((state) => ({
        isLoadingUser: isExecuting(UserOpType.LoadUser)(state),
        isUpdatingUser: isExecuting(UserOpType.UpdateUser)(state),
        hasLoadUserError: hasError(UserOpType.LoadUser)(state),
        hasUpdateUserError: hasError(UserOpType.UpdateUser)(state),
    })),
    withMethods((
        store,
        queryService = inject(UserQueryService),
        commandService = inject(UserCommandService)
    ) => ({
        // Promise (not rxMethod): app init awaits this before routing; track already drives isLoadingUser.
        async loadUser(): Promise<User> {
            // track completes without emitting on failure; defaultValue avoids RxJS EmptyError.
            const user = await firstValueFrom(
                store.track(
                    queryService.getUser(),
                    UserOpType.LoadUser,
                    (user: User) => patchState(store, { user })
                ),
                { defaultValue: null }
            );
            if (!user) {
                throw new Error('Failed to load user');
            }
            return user;
        },
        updateUser: rxMethod<UpdateUserCommand>(
            switchMap((command) => store.track(
                commandService.updateUser(command).pipe(
                    switchMap(() => queryService.getUser())
                ),
                UserOpType.UpdateUser,
                (user: User) => patchState(store, { user })
            ))
        ),
    }))
);
